import type { OfflineBundle, OfflineOperation, OfflineRow } from './domainTypes';
import { CODIGO_LOTE_REGEX } from '@/lib/schemas/common';
import {
  applySensorEquipment,
  getSensorEquipment,
  type SensorEquipment,
} from '@/lib/sensorEquipment';
import type { NaveSensores } from '@/lib/queries/iot';
import {
  byDate,
  configuredPoints,
  dateNumber,
  decimal,
  detail,
  evolution,
  feedCurve,
  forCamada,
  liquidationSnapshot,
  madridDate,
  number,
  preparation,
  preview,
  row,
  rows,
  sampleMean,
  savedLiquidation,
  sum,
  text,
  weightCurve,
} from './derived';

const childTables: Record<string, keyof OfflineBundle['tables']> = {
  'entradas-animales': 'entradas',
  'lotes-nave': 'lotes',
  'entregas-pienso': 'entregas',
  salidas: 'salidas',
  pesajes: 'pesajes',
  'registros-diarios': 'registros',
};
const permissions: Record<string, string> = {
  naves: 'nave',
  camadas: 'camada',
  lotes: 'lote_nave',
  entradas: 'entrada_animales',
  registros: 'registro_diario',
  observaciones: 'registro_diario',
  pesajes: 'pesaje',
  entregas: 'entrega_pienso',
  salidas: 'salida',
  liquidaciones: 'liquidacion',
  curva_peso: 'curva_estandar',
  curva_pienso: 'entrega_pienso',
  liquidacion: 'liquidacion_configuracion',
};
function available(b: OfflineBundle, ...resources: string[]): boolean {
  const denied = resources.find((r) =>
    b.unavailable.some(
      (v) => v === r || v === permissions[r] || v.endsWith(`/${r}`) || v.endsWith(`.${r}`),
    ),
  );
  if (denied)
    throw Object.assign(new Error(`No tienes permiso para consultar ${denied}.`), { status: 403 });
  return b.complete;
}
function sensorEquipmentInput(input: OfflineRow): SensorEquipment {
  const equipment = input.equipment;
  const fields = ['environment', 'ammonia', 'water', 'silo'];
  if (
    Object.keys(input).some((key) => !['equipment', 'confirm_deactivation'].includes(key)) ||
    (input.confirm_deactivation !== undefined && typeof input.confirm_deactivation !== 'boolean') ||
    !equipment ||
    typeof equipment !== 'object' ||
    Array.isArray(equipment) ||
    Object.keys(equipment).some((key) => !fields.includes(key))
  )
    throw Object.assign(new Error('Equipamiento de sensores inválido'), { status: 422 });
  const value = equipment as Record<string, unknown>;
  if (
    !Array.isArray(value.environment) ||
    value.environment.some(
      (station) => typeof station !== 'string' || !/^[A-Z]{1,16}$/.test(station),
    ) ||
    new Set(value.environment).size !== value.environment.length ||
    ['ammonia', 'water', 'silo'].some((key) => typeof value[key] !== 'boolean')
  )
    throw Object.assign(new Error('Equipamiento de sensores inválido'), { status: 422 });
  return structuredClone(equipment) as SensorEquipment;
}
function demoItems(bundle: OfflineBundle): OfflineRow[] {
  const value = bundle.iot.demo ?? bundle.iot.scenarios;
  if (value == null)
    throw Object.assign(
      new Error('El control de la demo IoT no está disponible para esta sesión.'),
      { status: 403 },
    );
  const response = row(value);
  return Array.isArray(response.items)
    ? rows(response.items)
    : Object.values(response)
        .map(row)
        .filter((item) => item.nave_id != null);
}
function parse(path: string) {
  const url = new URL(path, 'https://offline.invalid');
  return { parts: url.pathname.split('/').filter(Boolean), params: url.searchParams };
}
function found(items: OfflineRow[], id: number): OfflineRow {
  const r = items.find((r) => r.id === id);
  if (!r) throw Object.assign(new Error('Registro no encontrado'), { status: 404 });
  return r;
}
/** Private M6 counters are usable only inside exit reads and projections. */
function salidaSources(bundle: OfflineBundle): OfflineBundle {
  if (!bundle.salidas_contexto) return bundle;
  const sources = bundle.salidas_contexto;
  return {
    ...bundle,
    salidas_contexto: undefined,
    tables: { ...bundle.tables, ...sources },
    unavailable: bundle.unavailable.filter(
      (permission) => !Object.keys(sources).includes(permission),
    ),
  };
}
function readSalidaContext(bundle: OfflineBundle, selected: string | null) {
  if (!available(bundle, 'salidas')) return undefined;
  if (!bundle.salidas_contexto && bundle.unavailable.includes('camadas')) return undefined;
  const source = salidaSources(bundle);
  const camada =
    selected != null
      ? found(source.tables.camadas, Number(selected))
      : [...source.tables.camadas]
          .filter((c) => !c.cerrada)
          .sort((a, b) => number(b.id) - number(a.id))[0];
  if (!camada) return null;
  const f = forCamada(source, number(camada.id));
  return {
    camada: Object.fromEntries(
      ['id', 'codigo_lote', 'sexo', 'fecha_entrada', 'cerrada'].map((field) => [
        field,
        camada[field],
      ]),
    ),
    lotes: f.lotes.map((lote) => ({
      lote_id: lote.id,
      nave_id: lote.nave_id,
      nave_codigo:
        source.tables.naves.find((n) => n.id === lote.nave_id)?.codigo ?? lote.nave_codigo ?? '?',
      aves_actuales:
        number(lote.num_animales_asignados) -
        f.registros
          .filter((r) => r.lote_nave_id === lote.id)
          .reduce((total, r) => total + number(r.bajas_muerte) + number(r.bajas_triaje), 0) -
        sum(
          f.salidas.flatMap((s) => rows(s.lineas)).filter((line) => line.lote_nave_id === lote.id),
          'unidades_cargadas',
        ).toNumber(),
    })),
  };
}
export function resourceName(path: string): string | undefined {
  const { parts: p } = parse(path);
  if (p[0] !== 'v1') return undefined;
  if (p[1] === 'catalogos')
    return p[2] === 'curva-esperada'
      ? 'curva_peso'
      : p[2] === 'curva-pienso'
        ? 'curva_pienso'
        : p[2] === 'liquidacion'
          ? 'liquidacion'
          : p[2]?.replaceAll('-', '_');
  if (p[1] === 'iot') return 'iot';
  return p[3] ? (childTables[p[3]] ?? (p[3] === 'liquidacion' ? 'liquidaciones' : p[1])) : p[1];
}
export function scopeForPath(bundle: OfflineBundle, path: string, _method = 'GET'): string {
  const { parts: p, params } = parse(path);
  if (p[1] === 'catalogos')
    return p[2] === 'liquidacion'
      ? 'liquidacion_configuracion'
      : `${p[2] === 'curva-esperada' ? 'curva_peso' : 'curva_pienso'}:${params.get('sexo') ?? 'machos'}`;
  if (p[1] === 'iot' && p[2] === 'demo') return `iot_demo:${p[4]}`;
  if (p[1] === 'naves') return 'naves';
  if (p[1] === 'lotes-nave') return `camada:${found(bundle.tables.lotes, Number(p[2])).camada_id}`;
  if (p[1] === 'camadas') return p.length === 2 ? 'camadas' : `camada:${p[2]}`;
  throw new Error('Ruta offline no admitida');
}
/** Reads always derive from confirmed sources plus the caller's pending projection. */
export function readOffline(
  bundle: OfflineBundle,
  path: string,
  body?: unknown,
  today = madridDate(),
): unknown | undefined {
  const { parts: p, params } = parse(path);
  if (p[0] !== 'v1' || !bundle.complete) return undefined;
  if (p[1] === 'camadas' && p[2] === 'salidas-contexto')
    return readSalidaContext(bundle, params.get('camada_id'));
  if (p[1] === 'camadas' && p[3] === 'salidas') {
    if (!available(bundle, 'salidas')) return undefined;
    if (bundle.salidas_contexto) return readOffline(salidaSources(bundle), path, body, today);
    if (bundle.unavailable.includes('camadas')) return undefined;
  }
  const t = bundle.tables;
  if (p[1] === 'naves')
    return available(bundle, 'naves')
      ? p[2]
        ? found(t.naves, Number(p[2]))
        : [...t.naves].sort((a, b) => text(a.codigo).localeCompare(text(b.codigo)))
      : undefined;
  if (p[1] === 'catalogos') {
    const name = resourceName(path)!;
    if (!available(bundle, name)) return undefined;
    if (name === 'liquidacion') return bundle.catalogos.liquidacion ?? undefined;
    if (name === 'curva_peso' || name === 'curva_pienso') {
      const sexo = params.get('sexo') ?? 'machos',
        points = configuredPoints(bundle.catalogos[name][sexo]);
      return {
        sexo,
        puntos: points,
        edad_actual_dias: null,
        origen: points.length ? 'ajustes' : 'vacio',
        camadas_historico: 0,
      };
    }
    return bundle.catalogos[name as 'tipos_pienso' | 'granjas_origen'];
  }
  if (p[1] === 'iot') {
    if (!available(bundle, 'iot')) return undefined;
    if (p[2] === 'naves') {
      if (p[4] === 'sensores') {
        const raw = bundle.iot.sensores[p[3]] as NaveSensores | undefined;
        const nave = t.naves.find((n) => n.id === Number(p[3]));
        if (
          raw === undefined &&
          number(nave?.id) < 0 &&
          nave?.local_pending === true &&
          nave.sensor_equipment == null
        )
          return applySensorEquipment(
            undefined,
            { environment: [], ammonia: false, water: false, silo: false },
            Number(p[3]),
          );
        return nave?.sensor_equipment != null
          ? applySensorEquipment(raw, nave.sensor_equipment as SensorEquipment, Number(p[3]))
          : raw;
      }
      return p[4] === 'temperatura' ? bundle.iot.temperatura[p[3]] : undefined;
    }
    if (p[2] === 'demo' && p[3] === 'scenarios') return { items: demoItems(bundle) };
    return undefined;
  }
  if (p[1] === 'lotes-nave') {
    available(bundle, 'lotes');
    const id = Number(p[2]),
      lote = found(t.lotes, id),
      name = p[3];
    if (name === 'curva-estandar')
      return available(bundle, 'camadas', 'lotes', 'pesajes', 'curva_peso')
        ? weightCurve(bundle, number(lote.camada_id), today)
        : undefined;
    const table = childTables[name];
    if (!table || !available(bundle, table)) return undefined;
    const values = t[table]
      .filter(
        (r) =>
          number(r.lote_nave_id) === id &&
          (!params.has('fecha_desde') || text(r.fecha) >= params.get('fecha_desde')!) &&
          (!params.has('fecha_hasta') || text(r.fecha) <= params.get('fecha_hasta')!),
      )
      .sort(byDate);
    return p[4] ? found(values, Number(p[4])) : values;
  }
  if (p[1] !== 'camadas' || !available(bundle, 'camadas')) return undefined;
  if (!p[2]) {
    if (!available(bundle, 'liquidaciones')) return undefined;
    if (params.has('month') && !params.has('year'))
      throw new Error("El filtro 'month' requiere el filtro 'year'");
    const limit = Number(params.get('limit') ?? 50),
      offset = Number(params.get('offset') ?? 0);
    if (
      !Number.isInteger(limit) ||
      limit < 1 ||
      limit > 200 ||
      !Number.isInteger(offset) ||
      offset < 0
    )
      throw new Error('Paginación inválida');
    let values = t.camadas.filter(
      (c) =>
        (!params.has('cerrada') || !!c.cerrada === (params.get('cerrada') === 'true')) &&
        (!params.has('year') || text(c.fecha_entrada).slice(0, 4) === params.get('year')) &&
        (!params.has('month') ||
          number(text(c.fecha_entrada).slice(5, 7)) === Number(params.get('month'))) &&
        (!params.has('sexo') || c.sexo === params.get('sexo')) &&
        (!params.has('codigo_lote') ||
          text(c.codigo_lote).toLowerCase().includes(params.get('codigo_lote')!.toLowerCase())),
    );
    values = values.sort((a, b) =>
      params.get('order') === 'fecha_cierre_desc'
        ? Number(!!b.cerrada) - Number(!!a.cerrada) ||
          text(b.fecha_cierre).localeCompare(text(a.fecha_cierre)) ||
          number(b.id) - number(a.id)
        : Number(!!a.cerrada) - Number(!!b.cerrada) ||
          text(b.fecha_entrada).localeCompare(text(a.fecha_entrada)) ||
          number(b.id) - number(a.id),
    );
    return {
      items: values.slice(offset, offset + limit).map((c) => {
        const saved = savedLiquidation(bundle, number(c.id));
        return {
          ...c,
          liquidacion: saved
            ? {
                total_eur: row(row(saved.snapshot).resultado).total_eur,
                fecha_guardado: saved.updated_at,
                local_pending: !!saved.local_pending,
              }
            : null,
        };
      }),
      total: values.length,
      limit,
      offset,
    };
  }
  const cid = Number(p[2]),
    camada = found(t.camadas, cid);
  if (!p[3]) return camada;
  if (p[3] === 'detalle')
    return available(
      bundle,
      'lotes',
      'registros',
      'pesajes',
      'entregas',
      'salidas',
      'liquidaciones',
    )
      ? detail(bundle, cid, today)
      : undefined;
  if (p[3] === 'liquidacion') {
    if (
      !available(
        bundle,
        'liquidaciones',
        'liquidacion',
        'registros',
        'entregas',
        'salidas',
        'lotes',
      )
    )
      return undefined;
    if (p[4] === 'evolucion')
      return available(bundle, 'entradas', 'pesajes', 'curva_peso')
        ? evolution(bundle, cid, today, params.get('hasta') ?? undefined)
        : undefined;
    if (p[4] === 'preview') return preview(bundle, cid, body);
    return preparation(bundle, cid);
  }
  if (p[3] === 'curva-pienso')
    return available(bundle, 'entregas', 'curva_pienso')
      ? feedCurve(bundle, cid, today)
      : undefined;
  if (p[3] === 'consumo-pienso') {
    if (!available(bundle, 'entregas')) return undefined;
    const total = sum(
        t.entregas.filter((e) => number(e.camada_id) === cid),
        'kg',
      ).toNumber(),
      left =
        camada.cerrada && camada.kg_sobrante_pienso != null
          ? number(camada.kg_sobrante_pienso)
          : null;
    return {
      kg_total_entregado: total,
      kg_sobrante: left,
      kg_consumido: left == null ? null : total - left,
    };
  }
  if (p[3] === 'registros-diarios') {
    if (!available(bundle, 'lotes', 'registros', 'observaciones')) return undefined;
    const fecha = params.get('fecha');
    if (!fecha) return undefined;
    dateNumber(fecha);
    const f = forCamada(bundle, cid),
      regs = f.registros.filter((r) => r.fecha === fecha),
      obs = f.observaciones.find((r) => r.fecha === fecha);
    return {
      fecha,
      explotacion_id: bundle.explotacion_id,
      revision: bundle.revisions[`camada:${cid}`] ?? '',
      local_pending: regs.some((r) => r.local_pending) || !!obs?.local_pending,
      observaciones: obs?.observaciones ?? null,
      registros: f.lotes.map((l) => {
        const r = regs.find((r) => number(r.lote_nave_id) === l.id);
        return {
          id: r?.id ?? null,
          lote_nave_id: l.id,
          nave_codigo: t.naves.find((n) => n.id === l.nave_id)?.codigo ?? l.nave_codigo ?? '?',
          fecha,
          bajas_muerte: number(r?.bajas_muerte),
          bajas_triaje: number(r?.bajas_triaje),
        };
      }),
    };
  }
  const table = childTables[p[3]];
  if (!table || !available(bundle, table)) return undefined;
  const values = t[table]
    .filter((r) => number(r.camada_id) === cid)
    .sort((a, b) =>
      table === 'salidas'
        ? text(a.fecha_carga).localeCompare(text(b.fecha_carga)) || number(a.id) - number(b.id)
        : byDate(a, b),
    );
  if (table === 'lotes')
    return values.map((l) => ({
      ...l,
      nave_codigo: t.naves.find((n) => n.id === l.nave_id)?.codigo ?? l.nave_codigo ?? '?',
    }));
  return p[4] ? found(values, Number(p[4])) : values;
}
function dailyBalances(bundle: OfflineBundle, cid: number) {
  const f = forCamada(bundle, cid),
    daily = new Map<string, number>(),
    add = (date: unknown, amount: number) => {
      if (!amount) return;
      dateNumber(date);
      const key = text(date);
      daily.set(key, (daily.get(key) ?? 0) + amount);
    };
  for (const e of f.entradas) add(e.fecha, number(e.num_animales));
  for (const r of f.registros) add(r.fecha, -number(r.bajas_muerte) - number(r.bajas_triaje));
  for (const s of f.salidas) add(s.fecha_carga, -number(s.unidades_cargadas_total));
  return daily;
}
function validateBalances(bundle: OfflineBundle, cid: number, previous?: OfflineBundle) {
  if (!available(bundle, 'camadas', 'entradas', 'lotes', 'registros', 'salidas'))
    throw new Error('Faltan fuentes para validar el balance');
  const f = forCamada(bundle, cid),
    bajas = (rs: OfflineRow[]) =>
      rs.reduce((a, r) => a + number(r.bajas_muerte) + number(r.bajas_triaje), 0),
    lines = f.salidas.flatMap((s) => rows(s.lineas));
  if (
    number(f.camada.num_animales_total) -
      bajas(f.registros) -
      sum(f.salidas, 'unidades_cargadas_total').toNumber() <
    0
  )
    throw new Error('Las bajas y salidas superan las aves entradas');
  for (const l of f.lotes)
    if (
      number(l.num_animales_asignados) -
        bajas(f.registros.filter((r) => number(r.lote_nave_id) === l.id)) -
        sum(
          lines.filter((s) => number(s.lote_nave_id) === l.id),
          'unidades_cargadas',
        ).toNumber() <
      0
    )
      throw new Error('Las bajas y salidas superan la asignación de la nave');
  const daily = dailyBalances(bundle, cid),
    before = previous ? dailyBalances(previous, cid) : new Map<string, number>(),
    dates = [...new Set([...daily.keys(), ...before.keys()])].sort();
  if (previous) {
    let improvement = 0;
    if (
      dates.every((date) => {
        improvement += (daily.get(date) ?? 0) - (before.get(date) ?? 0);
        return improvement >= 0;
      })
    )
      return;
  }
  let balance = 0;
  for (const date of dates) {
    balance += daily.get(date) ?? 0;
    if (balance < 0)
      throw new Error(`Saldo de animales negativo el ${date}: faltan ${-balance} aves recibidas`);
  }
}
function integer(value: unknown, min = 0) {
  if (!Number.isInteger(value) || number(value) < min)
    throw new Error('Cantidad de animales inválida');
}
function positiveDecimal(value: unknown, allowZero = false) {
  if (value == null || decimal(value).compare(0) < (allowZero ? 0 : 1))
    throw new Error('Peso inválido');
}
function validateDate(r: OfflineRow, field: string, c: OfflineRow) {
  dateNumber(r[field]);
  if (text(r[field]) < text(c.fecha_entrada))
    throw new Error('La fecha no puede ser anterior a la entrada de la camada');
}
/** Pure optimistic projection. Server permissions and final constraints remain authoritative. */
export function projectOperation(
  bundle: OfflineBundle,
  operation: OfflineOperation,
): OfflineBundle {
  const { parts } = parse(operation.path);
  if (parts[1] === 'camadas' && parts[3] === 'salidas' && bundle.salidas_contexto) {
    available(bundle, 'salidas');
    const projection = projectOperation(salidaSources(bundle), operation);
    return { ...bundle, tables: { ...bundle.tables, salidas: projection.tables.salidas } };
  }
  const b = structuredClone(bundle),
    { parts: p, params } = parse(operation.path),
    method = operation.method.toUpperCase(),
    input = row(operation.body),
    table = resourceName(operation.path);
  if (
    p[0] !== 'v1' ||
    !['POST', 'PATCH', 'PUT', 'DELETE'].includes(method) ||
    !table ||
    !available(b, table)
  )
    throw new Error('Operación offline no disponible');
  const timestamp = operation.createdAt ?? new Date().toISOString(),
    existingIds = Object.values(b.tables)
      .flatMap((rs) => rs.map((r) => number(r.id)))
      .filter((id) => id < 0);
  let nextId = operation.localId ?? Math.min(-1, ...existingIds.map((id) => id - 1));
  const make = (body: OfflineRow): OfflineRow => ({
    id: nextId--,
    explotacion_id: b.explotacion_id,
    created_at: timestamp,
    updated_at: timestamp,
    local_pending: true,
    ...body,
  });
  if (p[1] === 'catalogos') {
    if (method !== 'PUT') throw new Error('Operación de catálogo no admitida');
    if (p[2] === 'liquidacion') b.catalogos.liquidacion = structuredClone(input);
    else if (p[2] === 'curva-esperada' || p[2] === 'curva-pienso') {
      const name = p[2] === 'curva-esperada' ? 'curva_peso' : 'curva_pienso',
        sexo = params.get('sexo') ?? 'machos',
        points = structuredClone(rows(input.puntos));
      if (!['machos', 'hembras'].includes(sexo)) throw new Error('Sexo inválido');
      if (!points.length) throw new Error('La curva necesita al menos un punto');
      const ages = new Set<number>();
      for (const [index, point] of points.entries()) {
        if (
          !Number.isInteger(point.edad_dias) ||
          number(point.edad_dias) < (name === 'curva_pienso' ? 1 : 0) ||
          number(point.edad_dias) > 2_147_483_647
        )
          throw new Error('Edad de curva inválida');
        if (ages.has(number(point.edad_dias))) throw new Error('Edad repetida');
        ages.add(number(point.edad_dias));
        const value = point[name === 'curva_peso' ? 'peso_medio_kg' : 'kg_acumulados'];
        if (!Number.isFinite(Number(value))) throw new Error('Valor de curva inválido');
        positiveDecimal(value, name === 'curva_pienso');
        if (
          name === 'curva_peso' &&
          (decimal(value).compare('0.001') < 0 || decimal(value).compare('999.999') > 0)
        )
          throw new Error('El peso debe estar entre 0.001 y 999.999 kg');
        if (name === 'curva_pienso' && decimal(value).compare('99999999.99') > 0)
          throw new Error('El pienso acumulado no puede superar 99999999.99 kg');
        const previous = points[index - 1];
        if (
          name === 'curva_pienso' &&
          previous &&
          (number(point.edad_dias) <= number(previous.edad_dias) ||
            decimal(value).compare(decimal(previous.kg_acumulados)) < 0)
        )
          throw new Error('La curva debe tener días crecientes y kg acumulados no decrecientes');
      }
      b.catalogos[name][sexo] = points.sort((a, c) => number(a.edad_dias) - number(c.edad_dias));
    } else throw new Error('Catálogo no modificable offline');
    return b;
  }
  if (p[1] === 'iot') {
    if (method !== 'PUT' || p[2] !== 'demo' || p[3] !== 'naves' || p[5] !== 'scenario')
      throw new Error('Operación IoT no admitida');
    const values = Array.isArray(input.scenarios) ? (input.scenarios as string[]) : [],
      allowed = [
        'normal',
        'temperature_low',
        'temperature_high',
        'humidity_high',
        'ammonia_high',
        'water_stalled',
        'silo_low',
      ];
    if (
      values.some((v) => !allowed.includes(v)) ||
      new Set(values).size !== values.length ||
      (values.includes('normal') && values.length > 1) ||
      (values.includes('temperature_low') && values.includes('temperature_high'))
    )
      throw new Error('Escenarios incompatibles');
    const items = demoItems(b).filter((r) => number(r.nave_id) !== Number(p[4]));
    items.push({ nave_id: Number(p[4]), scenarios: values, local_pending: true });
    b.iot.demo = { items };
    return b;
  }
  if (p[1] === 'naves') {
    const id = Number(p[2]);
    if (method === 'POST' && !p[2]) {
      const codigo = text(input.codigo).trim();
      if (!/^[A-Za-z0-9-]{1,50}$/.test(codigo) || b.tables.naves.some((n) => n.codigo === codigo))
        throw new Error('Código de nave inválido o repetido');
      b.tables.naves.push(
        make({
          superficie_m2: null,
          capacidad_animales: null,
          tipo_ventilacion: null,
          observaciones: null,
          fecha_alta: null,
          ...input,
          codigo,
          nombre: text(input.nombre || codigo).trim(),
        }),
      );
    } else {
      const existing = found(b.tables.naves, id);
      if (method === 'PUT' && p[3] === 'sensores' && p.length === 4) {
        const equipment = sensorEquipmentInput(input);
        const current = getSensorEquipment(
          { sensor_equipment: existing.sensor_equipment as SensorEquipment | null | undefined },
          b.iot.sensores[String(id)] as NaveSensores | undefined,
        );
        const removing =
          current.environment.some((station) => !equipment.environment.includes(station)) ||
          (['ammonia', 'water', 'silo'] as const).some((key) => current[key] && !equipment[key]);
        if (removing && input.confirm_deactivation !== true)
          throw Object.assign(new Error('Confirma la retirada de supervisión de estos sensores.'), {
            status: 409,
            code: 'sensor_deactivation_confirmation_required',
          });
        Object.assign(existing, {
          sensor_equipment: equipment,
          sensor_equipment_local_pending: true,
          updated_at: timestamp,
          local_pending: true,
        });
      } else if (method === 'DELETE') {
        if (b.tables.lotes.some((l) => number(l.nave_id) === id))
          throw new Error('La nave tiene asignaciones');
        b.tables.naves = b.tables.naves.filter((n) => n.id !== id);
      } else if (method === 'PATCH') {
        if (input.codigo && b.tables.naves.some((n) => n.id !== id && n.codigo === input.codigo))
          throw new Error('Código de nave repetido');
        Object.assign(existing, input, { updated_at: timestamp, local_pending: true });
      } else throw new Error('Operación de nave no admitida');
    }
    return b;
  }
  if (p[1] === 'camadas' && !p[2]) {
    if (method !== 'POST') throw new Error('Operación de camada no admitida');
    if (b.tables.camadas.some((c) => !c.cerrada)) throw new Error('Ya existe una camada activa');
    if (
      !CODIGO_LOTE_REGEX.test(text(input.codigo_lote)) ||
      !['machos', 'hembras'].includes(text(input.sexo))
    )
      throw new Error('Datos de camada inválidos');
    dateNumber(input.fecha_entrada);
    b.tables.camadas.push(
      make({
        fase: 'cebo',
        granja_origen_id: null,
        incubadora_origen: null,
        num_animales_total: 0,
        kg_entrada_total: '0',
        edad_dias_entrada: null,
        peso_medio_kg_entrada: null,
        fecha_prevista_salida: null,
        camada_recria_origen_id: null,
        cerrada: false,
        fecha_cierre: null,
        motivo_justificacion_descuadre: null,
        kg_sobrante_pienso: null,
        ajuste_carga_inicial_eur:
          row(b.catalogos.liquidacion).ajuste_carga_predeterminado_eur ?? '0',
        bonificacion_inicial_eur:
          row(b.catalogos.liquidacion).bonificacion_predeterminada_eur ?? '0',
        ...input,
      }),
    );
    return b;
  }
  const lote = p[1] === 'lotes-nave' ? found(b.tables.lotes, Number(p[2])) : undefined,
    cid = lote ? number(lote.camada_id) : Number(p[2]),
    f = forCamada(b, cid),
    c = f.camada;
  if (p[1] === 'camadas' && !p[3]) {
    if (method === 'DELETE') {
      if (
        c.cerrada ||
        f.lotes.length ||
        f.entradas.length ||
        f.registros.length ||
        f.entregas.length ||
        f.salidas.length ||
        savedLiquidation(b, cid)
      )
        throw new Error('La camada tiene dependencias o está cerrada');
      b.tables.camadas = b.tables.camadas.filter((r) => r.id !== cid);
      return b;
    }
    if (method !== 'PATCH') throw new Error('Operación de camada no admitida');
    if (input.fecha_entrada) {
      dateNumber(input.fecha_entrada);
      const earliest = [
        ...f.entradas,
        ...f.registros,
        ...f.pesajes,
        ...f.entregas,
        ...f.salidas.map((s) => ({ ...s, fecha: s.fecha_carga })),
      ]
        .map((r) => text(r.fecha))
        .sort()[0];
      if (earliest && text(input.fecha_entrada) > earliest)
        throw new Error('La fecha de entrada supera registros existentes');
    }
    Object.assign(c, input, { updated_at: timestamp, local_pending: true });
    return b;
  }
  if (p[3] === 'reabrir') {
    if (
      method !== 'POST' ||
      !c.cerrada ||
      b.tables.camadas.some((other) => other.id !== cid && !other.cerrada)
    )
      throw new Error('No se puede reabrir la camada');
    Object.assign(c, {
      cerrada: false,
      fecha_cierre: null,
      updated_at: timestamp,
      local_pending: true,
    });
    return b;
  }
  if (p[3] === 'cerrar') {
    if (
      method !== 'POST' ||
      c.cerrada ||
      !f.salidas.length ||
      f.salidas.some((s) => s.kg_confirmados_destino == null)
    )
      throw new Error('No se puede cerrar sin salidas con peso');
    if (text(c.fecha_entrada) > madridDate(timestamp))
      throw new Error('No se puede cerrar antes de la fecha de entrada');
    validateBalances(b, cid);
    const balance =
      number(c.num_animales_total) -
      f.registros.reduce((a, r) => a + number(r.bajas_muerte) + number(r.bajas_triaje), 0) -
      sum(f.salidas, 'unidades_cargadas_total').toNumber();
    if (balance > 0 && !text(input.motivo_justificacion_descuadre).trim())
      throw new Error('El descuadre requiere justificación');
    const left = input.kg_sobrante_pienso ?? c.kg_sobrante_pienso;
    if (
      left != null &&
      (decimal(left).compare(0) < 0 ||
        decimal(left).compare('99999999.99') > 0 ||
        decimal(left).compare(sum(f.entregas, 'kg')) > 0)
    )
      throw new Error('Pienso sobrante inválido');
    Object.assign(c, {
      cerrada: true,
      fecha_cierre: timestamp,
      updated_at: timestamp,
      local_pending: true,
    });
    if (input.motivo_justificacion_descuadre != null)
      c.motivo_justificacion_descuadre = input.motivo_justificacion_descuadre;
    if (input.kg_sobrante_pienso != null) c.kg_sobrante_pienso = input.kg_sobrante_pienso;
    return b;
  }
  if (p[3] === 'liquidacion') {
    if (method !== 'PUT' || !c.cerrada)
      throw new Error('La camada debe estar cerrada para guardar la liquidación');
    const value = preview(b, cid, input),
      snapshot = liquidationSnapshot(value),
      saved = savedLiquidation(b, cid);
    if (input.version_esperada != null && number(input.version_esperada) !== number(saved?.version))
      throw new Error('La liquidación ha cambiado');
    b.tables.liquidaciones = b.tables.liquidaciones.filter((r) => number(r.camada_id) !== cid);
    b.tables.liquidaciones.push({
      camada_id: cid,
      version: number(saved?.version) + 1,
      updated_at: timestamp,
      snapshot,
      local_pending: true,
    });
    return b;
  }
  if (c.cerrada) throw new Error('La camada está cerrada');
  const child = p[3],
    target = childTables[child];
  if (!target) throw new Error('Recurso no modificable offline');
  if (p[1] === 'camadas' && child === 'registros-diarios') {
    if (method !== 'POST') throw new Error('Operación de parte no admitida');
    validateDate(input, 'fecha', c);
    if (dateNumber(input.fecha) > dateNumber(madridDate(timestamp)) + 1)
      throw new Error('La fecha del parte no puede ser posterior a hoy + 1');
    if (!f.lotes.length) throw new Error('La camada no tiene naves asignadas');
    if (!Array.isArray(input.partes)) throw new Error('partes debe ser una lista');
    const parts = rows(input.partes);
    if (!parts.length && !Object.hasOwn(input, 'observaciones'))
      throw new Error('partes vacío requiere observaciones explícitas');
    if (new Set(parts.map((r) => number(r.lote_nave_id))).size !== parts.length)
      throw new Error('lote_nave_id duplicado en partes');
    for (const part of parts) {
      const id = number(part.lote_nave_id);
      if (!f.lotes.some((l) => l.id === id)) throw new Error('Lote de otra camada');
      integer(part.bajas_muerte ?? 0);
      integer(part.bajas_triaje ?? 0);
      const previous = b.tables.registros.find(
        (r) => number(r.lote_nave_id) === id && r.fecha === input.fecha,
      );
      if (previous)
        Object.assign(previous, {
          bajas_muerte: part.bajas_muerte ?? 0,
          bajas_triaje: part.bajas_triaje ?? 0,
          updated_at: timestamp,
          local_pending: true,
        });
      else
        b.tables.registros.push(
          make({
            lote_nave_id: id,
            fecha: input.fecha,
            bajas_muerte: part.bajas_muerte ?? 0,
            bajas_triaje: part.bajas_triaje ?? 0,
            agua_litros: null,
            temp_min_c: null,
            temp_max_c: null,
            observaciones: null,
            client_uuid: null,
          }),
        );
    }
    const existing = b.tables.observaciones.find(
      (r) => number(r.camada_id) === cid && r.fecha === input.fecha,
    );
    if (existing)
      Object.assign(existing, { observaciones: input.observaciones ?? null, local_pending: true });
    else
      b.tables.observaciones.push(
        make({ camada_id: cid, fecha: input.fecha, observaciones: input.observaciones ?? null }),
      );
    validateBalances(b, cid, bundle);
    return b;
  }
  const entityId = p[4] ? Number(p[4]) : undefined,
    collection = b.tables[target],
    existing =
      entityId == null
        ? undefined
        : found(
            collection.filter((r) =>
              lote ? number(r.lote_nave_id) === lote.id : number(r.camada_id) === cid,
            ),
            entityId,
          );
  if (method === 'DELETE') {
    if (!existing) throw new Error('Registro no encontrado');
    if (
      target === 'lotes' &&
      (b.tables.registros.some((r) => number(r.lote_nave_id) === entityId) ||
        b.tables.pesajes.some((r) => number(r.lote_nave_id) === entityId) ||
        f.salidas.flatMap((s) => rows(s.lineas)).some((r) => number(r.lote_nave_id) === entityId))
    )
      throw new Error('El lote tiene registros dependientes');
    b.tables[target] = collection.filter((r) => r.id !== entityId);
  } else {
    if ((method === 'POST' && existing) || (method !== 'POST' && !existing))
      throw new Error('Operación de registro inválida');
    const defaults: Record<string, OfflineRow> = {
      entradas: { num_documento_traslado: null, observaciones: null },
      lotes: {},
      entregas: { num_albaran_procavi: null, observaciones: null },
      registros: {
        bajas_muerte: 0,
        bajas_triaje: 0,
        agua_litros: null,
        temp_min_c: null,
        temp_max_c: null,
        observaciones: null,
        client_uuid: null,
      },
      pesajes: {},
      salidas: {
        numero_camion: null,
        numero_albaran: null,
        kg_confirmados_destino: null,
        fecha_recepcion_peso: null,
        observaciones: null,
        anulada: false,
      },
    };
    const value = existing
      ? { ...existing, ...input, updated_at: timestamp, local_pending: true }
      : make({
          ...defaults[target],
          ...input,
          ...(lote ? { lote_nave_id: lote.id } : { camada_id: cid }),
        });
    if (target === 'entradas') {
      validateDate(value, 'fecha', c);
      integer(value.num_animales, 1);
      positiveDecimal(value.kg_totales, true);
      value.kg_totales = decimal(value.kg_totales).round(2, 'half_up').toString();
      if (
        f.entradas.some(
          (e) =>
            e.id !== existing?.id &&
            e.fecha === value.fecha &&
            (e.num_documento_traslado ?? null) === (value.num_documento_traslado ?? null),
        )
      )
        throw new Error('Entrada duplicada');
    }
    if (target === 'entregas') {
      validateDate(value, 'fecha', c);
      const kg = decimal(value.kg);
      if (kg.compare('0.01') < 0 || kg.compare('99999999.99') > 0)
        throw new Error('kg debe estar entre 0.01 y 99999999.99');
      if (!b.catalogos.tipos_pienso.some((t) => t.id === value.piensotipo_id))
        throw new Error('Tipo de pienso inexistente');
      if (
        value.num_albaran_procavi != null &&
        (typeof value.num_albaran_procavi !== 'string' ||
          Array.from(value.num_albaran_procavi).length > 100)
      )
        throw new Error('El albarán debe tener como máximo 100 caracteres');
      value.kg = kg.round(2, 'half_up').toString();
    }
    if (target === 'lotes') {
      integer(value.num_animales_asignados, 1);
      if (existing && input.nave_id != null && input.nave_id !== existing.nave_id)
        throw new Error('Los animales no cambian de nave');
      if (!b.tables.naves.some((n) => n.id === value.nave_id)) throw new Error('Nave inexistente');
      if (f.lotes.some((l) => l.id !== existing?.id && l.nave_id === value.nave_id))
        throw new Error('Nave ya asignada');
      value.nave_codigo = b.tables.naves.find((n) => n.id === value.nave_id)?.codigo;
    }
    if (target === 'registros') {
      validateDate(value, 'fecha', c);
      if (dateNumber(value.fecha) > dateNumber(madridDate(timestamp)) + 1)
        throw new Error('La fecha del parte no puede ser posterior a hoy + 1');
      integer(value.bajas_muerte ?? 0);
      integer(value.bajas_triaje ?? 0);
      if (
        f.registros.some(
          (r) =>
            r.id !== existing?.id &&
            r.lote_nave_id === value.lote_nave_id &&
            r.fecha === value.fecha,
        )
      )
        throw new Error('Parte duplicado');
    }
    if (target === 'pesajes') {
      validateDate(value, 'fecha', c);
      if (text(value.fecha) > madridDate(timestamp))
        throw new Error('La fecha del pesaje no puede ser futura');
      const weights = Array.isArray(value.pesos_g) ? value.pesos_g : [];
      if (!weights.length) throw new Error('Faltan pesos');
      for (const weight of weights) {
        integer(weight, 1);
        if (number(weight) >= 60000) throw new Error('Peso inválido (>= 60 kg)');
      }
      if (
        f.pesajes.some(
          (p) =>
            p.id !== existing?.id &&
            p.lote_nave_id === value.lote_nave_id &&
            p.fecha === value.fecha,
        )
      )
        throw new Error('Pesaje duplicado');
      value.num_pavos = weights.length;
      value.peso_medio_kg = sampleMean(value).toNumber();
    }
    if (target === 'salidas') {
      if (method === 'PUT') value.kg_confirmados_destino = input.kg_confirmados_destino ?? null;
      validateDate(value, 'fecha_carga', c);
      if (text(value.fecha_carga) > madridDate(timestamp))
        throw new Error('La fecha de carga no puede ser futura');
      for (const field of ['matadero', 'numero_camion', 'numero_albaran', 'observaciones'])
        if (typeof value[field] === 'string') value[field] = value[field].trim();
      if (!text(value.matadero)) throw new Error('Falta matadero');
      if (text(value.matadero).length > 200) throw new Error('Matadero demasiado largo');
      if (text(value.numero_camion).length > 100 || text(value.numero_albaran).length > 100)
        throw new Error('Camión o albarán demasiado largo');
      if (!value.numero_camion && !value.numero_albaran) throw new Error('Indica camión o albarán');
      if (input.fecha_recepcion_peso != null) {
        dateNumber(input.fecha_recepcion_peso);
        if (value.kg_confirmados_destino == null)
          throw new Error('fecha_recepcion_peso requiere kg_confirmados_destino');
      }
      const lines = rows(value.lineas);
      if (!lines.length || new Set(lines.map((l) => l.lote_nave_id)).size !== lines.length)
        throw new Error('Reparto de salida inválido');
      for (const line of lines) {
        integer(line.unidades_cargadas, 1);
        if (!f.lotes.some((l) => l.id === line.lote_nave_id))
          throw new Error('Lote de otra camada');
      }
      value.lineas = lines.map((line) => make({ ...line, salida_id: value.id }));
      value.unidades_cargadas_total = sum(lines, 'unidades_cargadas').toNumber();
      if (value.kg_confirmados_destino != null) {
        positiveDecimal(value.kg_confirmados_destino);
        value.fecha_recepcion_peso =
          input.fecha_recepcion_peso ??
          (p[5] === 'peso' ? madridDate(timestamp) : existing?.fecha_recepcion_peso) ??
          madridDate(timestamp);
        value.kg_confirmados_destino = decimal(value.kg_confirmados_destino)
          .round(2, 'half_up')
          .toString();
        value.peso_medio_kg = decimal(value.kg_confirmados_destino)
          .div(number(value.unidades_cargadas_total))
          .toNumber();
      } else {
        value.peso_medio_kg = null;
        value.fecha_recepcion_peso = null;
      }
    }
    if (existing) Object.assign(existing, value);
    else collection.push(value);
  }
  if (target === 'entradas') {
    const entries = b.tables.entradas.filter((e) => number(e.camada_id) === cid),
      count = sum(entries, 'num_animales').toNumber(),
      kg = sum(entries, 'kg_totales');
    Object.assign(c, {
      num_animales_total: count,
      kg_entrada_total: kg.toString(),
      peso_medio_kg_entrada: count ? kg.div(count).round(3, 'half_up').toNumber() : null,
      updated_at: timestamp,
      local_pending: true,
    });
  }
  if (['entradas', 'lotes', 'registros', 'salidas'].includes(target))
    validateBalances(b, cid, bundle);
  return b;
}
export function operationResult(bundle: OfflineBundle, operation: OfflineOperation): unknown {
  if (operation.method.toUpperCase() === 'DELETE') return null;
  const { parts: p } = parse(operation.path),
    table = resourceName(operation.path);
  if (p[1] === 'catalogos') return readOffline(bundle, operation.path);
  if (p[1] === 'iot') return demoItems(bundle).find((r) => number(r.nave_id) === Number(p[4]));
  if (p[3] === 'registros-diarios' && p[1] === 'camadas')
    return readOffline(bundle, `${operation.path}?fecha=${text(row(operation.body).fecha)}`);
  if (p[3] === 'cerrar' || p[3] === 'reabrir' || p[3] === 'liquidacion')
    return readOffline(bundle, p[3] === 'liquidacion' ? operation.path : `/v1/camadas/${p[2]}`);
  if (!table || !(table in bundle.tables)) return undefined;
  const id =
      operation.method.toUpperCase() === 'POST'
        ? operation.localId
        : p[4]
          ? Number(p[4])
          : p[2]
            ? Number(p[2])
            : undefined,
    values = bundle.tables[table as keyof OfflineBundle['tables']];
  return values.find((r) => r.id === id) ?? values.filter((r) => r.local_pending).at(-1);
}
